/**
 * The rail's two measured inputs: the origin delta and real card heights.
 *
 * The design handoff names the delta rule outright:
 *
 *   > Comment rail anchoring: block offsets are measured in the canvas and
 *   > translated into the rail's own coordinate space via a measured origin
 *   > delta. Do not reuse raw `offsetTop` — the rail track starts below its
 *   > toolbar.
 *
 * Here the "canvas" is an iframe, so the delta is the frame element's top
 * minus the track's top, both from `getBoundingClientRect()`. A block's
 * position in the frame's viewport is `top - scrollTop` (the overlay's
 * contract), so its position in track space is `top - scrollTop + delta` —
 * which the rail realises as cards placed at `top + delta` inside one layer
 * translated by `-scrollTop`.
 *
 * Heights are measured off the DOM (`[data-card]`), not assumed, so a long
 * thread or an open reply pushes the card below it by what it actually
 * occupies. Both measurements re-run after every render with a
 * compare-before-set guard — the mock's own trick — so the update loop
 * terminates.
 */

import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export function useRailGeometry(opts: {
  frame: React.RefObject<HTMLElement | null>;
  track: React.RefObject<HTMLElement | null>;
}): {
  originDelta: number;
  heights: Record<string, number>;
} {
  const { frame, track } = opts;
  // Seeded with the mock's own first guess so the first paint is close; the
  // measured value replaces it before anyone can scroll.
  const [originDelta, setOriginDelta] = useState(-95);
  const [heights, setHeights] = useState<Record<string, number>>({});
  const state = useRef({ originDelta, heights });
  state.current = { originDelta, heights };

  const measure = useCallback(() => {
    const frameEl = frame.current;
    const trackEl = track.current;
    if (!frameEl || !trackEl) return;

    const delta = Math.round(
      frameEl.getBoundingClientRect().top - trackEl.getBoundingClientRect().top
    );

    const next: Record<string, number> = {};
    trackEl.querySelectorAll('[data-card]').forEach((el) => {
      const id = el.getAttribute('data-card');
      if (id) next[id] = (el as HTMLElement).offsetHeight;
    });

    const cur = state.current;
    const keys = Object.keys(next);
    const sameHeights =
      keys.length === Object.keys(cur.heights).length && keys.every((k) => cur.heights[k] === next[k]);
    if (delta === cur.originDelta && sameHeights) return;

    if (delta !== cur.originDelta) setOriginDelta(delta);
    if (!sameHeights) setHeights(next);
  }, [frame, track]);

  // After every commit: new cards, opened replies and collapsed threads all
  // change heights, and none of them announce themselves.
  useLayoutEffect(() => {
    measure();
  });

  // The layout around the rail moves without a render on this side: the window
  // resizes, present mode collapses a pane, the frame's flex box settles.
  useLayoutEffect(() => {
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
    if (ro) {
      if (track.current) ro.observe(track.current);
      if (frame.current) ro.observe(frame.current);
    }
    return () => {
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
    };
  }, [frame, track, measure]);

  return { originDelta, heights };
}
